<template>
	<div :class="{ 'loading': loading }">

		<!-- True null -->
		<div v-if="!items.length && !loading && roleType == ''" class="flex-center pad-8 fs-13 color-gray fw-500 text-center">
			No messages to display
		</div>

		<template v-else>
			<div :class="{'border-bottom': isTrialing, 'mb-16': isTrialing }" class="flx-split pad-16 pt-8">
				<h5 class="color-violet">Recent Messages</h5>
			</div>
			<div v-if="!isTrialing" class="flex-split py-12 px-16 border-top-07 border-bottom-07 mb-12">
				<div class="fw-700 fs-14 color-violet">Filter by role:</div>
				<select v-model="roleType" class="stylized" style="width:189px">
					<option value="">All</option>
					<option value="photographerIds">Photographers</option>
					<option value="studioIds">Studios</option>
				</select>
			</div>
			
			<!-- Loading -->
			<template v-if="loading">
				<div v-for="index in 5" :key="`chatDroplistLoading${index}`" class="droplist-item flex-top">
					<div class="square-40 round skeleton mr-16"></div>
					<div class="grow pt-4">
						<div class="skeleton pt-12" style="width:100px"></div>
						<div class="skeleton pt-12 mt-12" style="width:100%"></div>
					</div>
				</div>
			</template>

			<!-- Messages -->
			<template v-else>
				<div
					v-for="item in items"
					:key="item.id"
					:class="['droplist-item flex-top', { 'trial-item': chatRequiresUpgrade(item) }]"
					@click="onChatClick(item)"
				>
					<template v-if="chatRequiresUpgrade(item)">
						<div class="square-40 round mr-16 bg-studio flex-center">
							<svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" fill="none" viewBox="0 0 13 12"><path stroke="#ED2644" stroke-width="1.5" d="M6.34336 8.0501C8.28936 8.0501 9.86836 6.4719 9.86836 4.525C9.86836 2.5782 8.28936 1 6.34336 1C4.39636 1 2.81836 2.5782 2.81836 4.525C2.81836 6.4719 4.39636 8.0501 6.34336 8.0501Z" clip-rule="evenodd"/><path stroke="#ED2644" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M1 11.1347C1.541 10.197 2.32 9.41838 3.258 8.87698C4.196 8.33568 5.26 8.05068 6.343 8.05078C7.426 8.05078 8.49 8.33578 9.428 8.87708C10.366 9.41848 11.145 10.1971 11.686 11.1348"/></svg>
						</div>
						<div class="color-violet no-overflow grow">
							<div class="flex-split">
								<div class="fs-14 lh-14 mt-4 fw-700">{{ item.job.event.name }}</div>
								<div class="color-gray no-wrap op-6">{{ timeAgo(item) }}</div>
							</div>
							<div class="color-gray fs-12 lh-14 mt-4 lh-16">Accept the job and <UpgradeButton class="button-inline fs-12 fw-500 color-blue" :job="item.job">upgrade your account</UpgradeButton> to read the message.</div>
						</div>
					</template>
					<template v-else>
						<ProfileAvatar :profile="item.chatUserProfile" size="40" class="square-40 mr-16"/>
						<div class="pt-4 color-violet no-overflow grow">
							<div class="flex-split fs-12 lh-14">
								<div class="user-name fw-500">{{ item.chatUserProfile.name }}</div>
								<div class="color-gray">{{ timeAgo(item) }}</div>
							</div>
							<div class="flex-split">
								<div class="no-overflow">
									<div class="fs-14 lh-14 mt-4 fw-700">{{ item.job.event.name }}</div>
									<div class="color-gray fs-12 lh-16 mt-4 truncate">{{ stripTags(item.lastMessage.content.text) }}</div>
								</div>
								<div v-if="item.unreadMessagesCount" class="unread-messages-count ml-8">{{ item.unreadMessagesCount }}</div>
							</div>
						</div>
					</template>
				</div>
				<div v-if="!items.length" class="flex-center pad-8 mb-8 fs-13 color-gray fw-500 text-center">
					No messages to display
				</div>
				<div v-if="items.length" class="text-center mt-12 lh-14 pb-8">
					<router-link :to="{ name: $routes.Messages.name }" class="fs-12">
						View All Conversations
					</router-link>
				</div> 
			</template>
		</template>
	</div>
</template>

<script>
import { DateTime } from 'luxon';
import { request } from '@/plugins/axios';

export default {
	name: 'ChatDroplist',
	data() {
		return {
			loading: true,
			items: [],
			pageSize: 5,
			roleType: '',
			today: new Date().setHours(0, 0, 0, 0),
		};
	},
	methods: {
		chatRequiresUpgrade(item) {
			const isFreeJob = this.freeJob && this.freeJob.id == item.job.id;

			// No upgrade if I created it
			if (item.job.creatorId == this.$activeUser.id) {
				return false;
			}
			
			// If I'm a trial account
			if (this.isTrialing && !isFreeJob && !item.isJobBooked) {
				return true;
			}

			return false;
		},
		fetchData(noLoading) {
			let roleQuery = '';
			if ( noLoading !== true ) {
				this.loading = true;
			}
			
			if ( this.roleType ) {
				roleQuery = this[this.roleType].join('&positionIds[]=');
				roleQuery = `&positionIds[]=${roleQuery}`;
			}

			request({
				url: `/chats/jobs/latest?limit=5${roleQuery}`,
				method: 'get',
			}).then((response) => {
				this.items = response.data.filter((item) => item && item.chat && item.chatUserProfile && item.job?.event);
				this.cache = this.items;
				this.loading = false;
			});
		},
		/**
		 * @param {ChatRecord | null | undefined} item
		 * @returns {void}
		 */
		onChatClick(item) {
			/** @type {ChatId | null | undefined} */
			let chatId;
			/** @type {JobId | null | undefined} */
			let jobId;
			if (
				item &&
				!this.chatRequiresUpgrade(item) &&
				(chatId = item.chat?.id) &&
				(jobId = item.job?.id)
			) {
				this.$router.push({ name: 'thread', params: { chatId, jobId } });
			}
		},
		stripTags(html) {
			const div = document.createElement('div');
			div.innerHTML = html;

			return div.innerText;
		},
		timeAgo(record) {
			const { chat, lastMessage } = record;
			/** @type {import('luxon').DateTime | null | undefined} */
			let date;
			/** @type {DateConvertible | null | undefined} */
			let source;
			if (lastMessage) {
				source = lastMessage.updatedAt || lastMessage.createdAt;
			}
			if (
				(source == null && (source = chat.updatedAt || chat.createdAt) == null) ||
				!(date = this.$toDateTime(source, { zone: this.$systemTimeZone }))
			) {
				return '';
			}
			/** @type {Intl.DateTimeFormatOptions} */
			let options;
			const today = DateTime.fromMillis(this.today, { zone: this.$systemTimeZone });
			if (date.year !== today.year) {
				options = DateTime.DATE_MED;
			} else if (date.month !== today.month || date.day !== today.day) {
				options = { day: 'numeric', month: 'short' };
			} else {
				options = DateTime.TIME_SIMPLE;
			}
			return date.toLocaleString(options);
		},
	},
	computed: {
		getViewAllMessagesText(total) {
			return total ? `View All ${total > 1 ? `${total} ` : ''}Conversations` : 'Message Center';
		},
		freeJob() {
			return this.$store.getters['jobs/freeJobData'].freeJob;
		},
		studioIds() {
			const roles = this.$store.getters['roles/list'].filter(r => r.slug == 'studio');
			return roles.map(r => r.id);
		},
		photographerIds() {
			const roles = this.$store.getters['roles/list'].filter(r => r.slug !== 'studio');
			return roles.map(r => r.id);
		},
		subscription() {
			return this.$store.getters['subscriptions/current'];
		},
		isTrialing() {
			return !!this.subscription?.isTrialing;
		},
		cache: {
			get() {
				return this.$store.getters['cache/dashboardMessages'];
			},
			set(items) {
				return this.$store.commit('cache/setCache', {
					name: 'dashboardMessages',
					items
				});
			},
		}
	},
	watch: {
		roleType: 'fetchData',
	},
	mounted() {
		if (this.cache) {
			this.items = this.cache.filter((item) => item && item.chat && item.chatUserProfile && item.job?.event);
		}
		this.loading = !this.cache.length;
		this.fetchData(true);
	},
};
</script>
